import { useState } from "react"
import { Button } from "react-bootstrap"
import timeService from "../../services/time/timeService"
import JSONData from "../JSONData/JSONData"
import { convertStringToJSON } from "../../Helpers/Strings/stringHelpers"

const MAX_VALUE_LENGTH = 100

const LINKED_SERVICE_DATA_TYPES = ['property', 'booking', 'building']
const LINKED_SERVICE_ID_PATH = /^linkedServices\.\d+\.linkedServiceId$/

const linkButtonStyle = { padding: 0, fontSize: 'inherit', verticalAlign: 'baseline' }

function getJoinedPath(path: any) {
    return Array.isArray(path) ? path.join('.') : path
}

function isLinkedServiceIdChange(dataType: string, joinedPath: string) {
    return LINKED_SERVICE_DATA_TYPES.includes(dataType) && LINKED_SERVICE_ID_PATH.test(joinedPath)
}

// linkedServiceId is stored as a JSON string; fall back to the raw value if it isn't valid JSON
function formatJsonValue(value: any) {
    if (!value) return ''
    try {
        return JSON.stringify(typeof value === 'string' ? JSON.parse(value) : value, null, 2)
    } catch {
        return String(value)
    }
}

function DiffValue(props: { label: string, value: string, formatted?: boolean }) {
    const { label, value, formatted } = props
    const [expanded, setExpanded] = useState(false)

    if (!value) return null

    if (formatted) {
        return (
            <>
                <b>{label}:</b>
                <span style={{ display: 'block', maxHeight: 250, overflow: 'auto' }}>{value}</span>
            </>
        )
    }

    const isLong = value.length > MAX_VALUE_LENGTH
    const displayValue = isLong && !expanded ? `${value.slice(0, MAX_VALUE_LENGTH)}...` : value

    return (
        <>
            <b>{label}:</b> {displayValue}
            {isLong && (
                <>
                    {' '}
                    <Button
                        variant="link"
                        size="sm"
                        style={linkButtonStyle}
                        onClick={() => setExpanded(!expanded)}
                    >
                        {expanded ? 'less' : 'more'}
                    </Button>
                </>
            )}
            <br />
        </>
    )
}

function DiffRow(props: { diff: any, dataType: string, myProperties: any }) {
    const { diff, dataType, myProperties } = props
    const { type, path, oldValue, value } = diff

    let oldValueString = oldValue ? JSON.stringify(oldValue) : ''
    let valueString = value ? JSON.stringify(value) : ''

    const joinedPath = getJoinedPath(path)
    const formatted = isLinkedServiceIdChange(dataType, joinedPath)

    if (formatted) {
        oldValueString = formatJsonValue(oldValue)
        valueString = formatJsonValue(value)
    }

    let displayOldValue = oldValue
    let displayValue = value

    if (joinedPath === 'propertyId') {
        const oldTitle = myProperties?.[oldValue]?.title
        const newTitle = myProperties?.[value]?.title
        if (oldTitle) {
            displayOldValue = oldTitle.split(' - ')[0].trim()
            oldValueString = displayOldValue
        }
        if (newTitle) {
            displayValue = newTitle.split(' - ')[0].trim()
            valueString = displayValue
        }
    }

    if (dataType === 'booking' && (joinedPath === 'checkIn' || joinedPath === 'checkOut')) {
        if (oldValue) {
            oldValueString = timeService.getFormattedTime(oldValue)
        }
        if (value) {
            valueString = timeService.getFormattedTime(value)
        }
    }

    // return (
    //     <pre className="mb-2 border-bottom pb-2" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
    //         {type}: {joinedPath} <br />
    //         <DiffValue label="Old" value={oldValueString} formatted={formatted} />
    //         <DiffValue label="New" value={valueString} formatted={formatted} />
    //     </pre>
    // )

    return (
        <JSONData
            data={{ 
                oldValue: formatted ? convertStringToJSON(oldValueString) : displayOldValue, 
                value: formatted ? convertStringToJSON(valueString) : displayValue
            }}
            maxHeight="200px"
            fontSize="10px"
            indentWidth={2}
            title={`${type}: ${joinedPath}`}
        />
    )
}

export default function DataLogDiffs(props: {
    diffs: any[],
    dataType: string,
    myProperties: any,
}) {
    const { diffs: allDiffs, dataType, myProperties } = props

    const diffs = (allDiffs || []).filter((diff: any) => getJoinedPath(diff.path) !== '__v')

    if (diffs.length === 0) return null

    return (
        <div>
            {diffs.map((diff: any, idx: number) => (
                <DiffRow
                    key={`${diff.type}-${diff.path}-${idx}`}
                    diff={diff}
                    dataType={dataType}
                    myProperties={myProperties}
                />
            ))}
        </div>
    )
}
